import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { useThrottleFn } from 'ahooks';
import { Bubble } from 'tea-component';
import GeneralImageNew from '@gp/components/GeneralImageNew';
import ReleasesModal from './ReleasesModal';
import { getElementStyleMathValue } from '@gp/utils/common';
import { goToProjectDetailsPage } from '@gp/utils/project';
import { formatMilestoneDateByStatus } from '@gp/utils/milestone';

import type { IBaseProps } from '@home/types/common';
import type { KeyMetricsCardListItemType } from '../../types';

import './index.scss';

export interface ISelfAdaptionInstance {
  openMoreModal: () => void;
  closeMoreModal: () => void;
}

interface ISelfAdaptionProps extends IBaseProps {
  cardTitle: string;
  data: KeyMetricsCardListItemType;
  setShowMoreBtn: React.Dispatch<React.SetStateAction<boolean>>;
}

// 直接展示projects的最大行数
const MAX_ROWS = 2;
// project图片宽度
const IMG_WIDTH = 30;
// project图片高度
const IMG_HEIGHT = 30;
// project的marginRight
const MARGIN_RIGHT = 16;
// project的marginBottom
const MARGIN_BOTTOM = 14;
// projectName的marginLeft，即projectName与project图片的间距
const NAME_MARGIN_LEFT = 6;
// projectName的宽度
const MIN_NAME_WIDTH = 130;
// project的宽度
const MIN_PROJECT_WIDTH = IMG_WIDTH + NAME_MARGIN_LEFT + MIN_NAME_WIDTH;

/**
 * 计算当前索引project的marginRight值
 * 纵向排列：
 *  marginRight值为0，其余为MARGIN_RIGHT
 * 非纵向排列：
 *  每一行的最后一个marginRignt值为0，否则为MARGIN_RIGHT
 * @param isVerticalLayout 是否为纵向排列
 * @param num 每一行展示的数量
 * @param index 当前索引
 */
const getMarginRight = (isVerticalLayout: boolean, num: number, index: number): number => {
  if (isVerticalLayout) return 0;
  return (index + 1) % num === 0 ? 0 : MARGIN_RIGHT;
};

/**
 * 计算当前索引project的marginBottom值
 * 纵向排列：
 *  最后一个marginBottom值为0，其余为MARGIN_BOTTOM
 * 非纵向排列：
 *  最后一行的marginBottom值为0，其余为MARGIN_BOTTOM
 *  如何计算最后一行的开始索引呢？
 *  用直接展示的数量 除以 每一行展示的个数，再对结果向上取整 得到行数，再用行数 减1，再乘以 每一行展示的个数 即为 最后一行的开始索引
 * @param isVerticalLayout 是否为纵向排列
 * @param num 每一行展示的数量
 * @param showCount 直接展示projects的数量
 * @param index 当前索引
 */
const getMarginBottom = (isVerticalLayout: boolean, num: number, showCount: number, index: number): number => {
  if (isVerticalLayout) return index === showCount - 1 ? 0 : MARGIN_BOTTOM;
  return index >= (Math.ceil(showCount / num) - 1) * num ? 0 : MARGIN_BOTTOM;
};

const SelfAdaption: React.ForwardRefRenderFunction<ISelfAdaptionInstance, ISelfAdaptionProps> = (props, ref) => {
  const { style, className, cardTitle, data, setShowMoreBtn } = props;
  const { domain, list } = data || {};

  // 卡片容器dom
  const dom = useRef<HTMLDivElement | null>(null);
  // 一行展示多少个project
  const [num, setNum] = useState(0);
  // 更多projects弹窗是否展示
  const [visible, setVisible] = useState(false);
  // 直接展示的projects数量
  const showCount = Math.min(list?.length || 0, num * MAX_ROWS);
  /**
   * 直接展示的projects是否纵向排列
   * 由于直接展示的数量 小于等于 MAX_ROWS，所以就直接纵向排列即可，因为纵向排列的话，横向空间更大，可以展示更多的project名称信息
   */
  const isVerticalLayout = showCount <= MAX_ROWS;

  // 计算卡片一行能展示多少个project
  const { run: resizeRun, flush: resizeFlush } = useThrottleFn(
    () => {
      if (!dom.current) return;
      const width = getElementStyleMathValue(dom.current, 'width');
      const num = Math.floor((width + MARGIN_RIGHT) / (IMG_WIDTH + NAME_MARGIN_LEFT + MIN_NAME_WIDTH + MARGIN_RIGHT));
      setNum(num);
      setShowMoreBtn(num * MAX_ROWS < list?.length);
    },
    { trailing: true, leading: true, wait: 300 },
  );

  useEffect(() => {
    resizeRun();
    resizeFlush();

    window.addEventListener('resize', resizeRun);

    return () => {
      window.removeEventListener('resize', resizeRun);
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openMoreModal: () => {
        setVisible(true);
      },
      closeMoreModal: () => {
        setVisible(false);
      },
    }),
    [],
  );

  return (
    <div ref={dom} style={style} className={classnames('dbg-self-adaption-wrapper', className)}>
      <div className="dbg-self-adaption">
        {Array.isArray(list) &&
          list.slice(0, num * MAX_ROWS).map((item, index) => {
            const { id: projectId, title: projectName, img, time, timeConfirm } = item || {};
            return (
              <div
                key={index}
                style={{
                  marginRight: getMarginRight(isVerticalLayout, num, index),
                  marginBottom: getMarginBottom(isVerticalLayout, num, showCount, index),
                  flexBasis: isVerticalLayout ? '100%' : MIN_PROJECT_WIDTH,
                }}
                className="dbg-self-adaption-context"
              >
                <div
                  className="dbg-self-adaption-img"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToProjectDetailsPage({
                      id: projectId || '',
                      name: projectName || '',
                      tab: domain || 'gameDetail',
                    });
                  }}
                >
                  <GeneralImageNew
                    style={{ marginRight: NAME_MARGIN_LEFT }}
                    width={IMG_WIDTH}
                    height={IMG_HEIGHT}
                    src={img || ''}
                  />
                </div>
                <Bubble
                  dark
                  placement="top"
                  content={
                    <div>
                      <div className="dbg-self-adaption-title">{projectName || '-'}</div>
                      <div className="dbg-self-adaption-time">
                        {formatMilestoneDateByStatus(time, timeConfirm) || '-'}
                      </div>
                    </div>
                  }
                >
                  <div
                    style={{ marginLeft: NAME_MARGIN_LEFT }}
                    className="dbg-self-adaption-name"
                    onClick={(e) => {
                      e.stopPropagation();
                      goToProjectDetailsPage({
                        id: projectId || '',
                        name: projectName || '',
                        tab: domain || 'gameDetail',
                      });
                    }}
                  >
                    {projectName}
                  </div>
                </Bubble>
              </div>
            );
          })}
      </div>
      <ReleasesModal
        domain={domain}
        title={cardTitle}
        visible={visible}
        data={list}
        close={() => {
          setVisible(!visible);
        }}
      />
    </div>
  );
};

// @ts-ignore
export default React.memo(React.forwardRef(SelfAdaption));
